import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import ReactModal from 'react-modal';

import LikeButton from '@components/like-button';
import ShareModal from '@components/modal/share-modal';
import Button from '@components/button';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  close_button,
  modal,
  modal_inner,
  modal_overlay,
  modal_body_class,
  header,
  social_buttons,
  like_button,
  share_button,
  address
} = styles;

// ---------------------------------------------------------

import Icon from '@components/icon';

// ---------------------------------------------------------

const FullScreenModal = (props) => {
  const {
    children,
    content,
    contentLabel,
    onModalClose,
    modalIsOpen,
    pageHasChat,
    canonicalUrl,
    sharingServicesCollection,
    property
  } = props;

  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);

  ReactModal.setAppElement('body');

  // ---------------------------------------------------------

  const overflowHidden = (modalIsOpen) => {
    if (modalIsOpen) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.removeProperty('overflow');
    }
  };

  const closeChatOnModelOpen = () => {
    if (pageHasChat && window?.LiveChatWidget) {
      window?.LiveChatWidget.call('hide');
    }
  };
  const showChatOnModalClose = () => {
    if (pageHasChat && window?.LiveChatWidget) {
      window?.LiveChatWidget.call('minimize');
    }
  };

  useEffect((modalIsOpen) => {
    overflowHidden(modalIsOpen);
  }, []);

  // ---------------------------------------------------------

  return (
    <ReactModal
      isOpen={modalIsOpen}
      overlayClassName={modal_overlay}
      contentLabel={contentLabel}
      className={modal}
      onRequestClose={onModalClose}
      shouldReturnFocusAfterClose={false}
      bodyOpenClassName={modal_body_class}
      preventScroll={true}
      onModalClose={onModalClose}
      onAfterOpen={closeChatOnModelOpen}
      onAfterClose={showChatOnModalClose}
    >
      <div className={header}>
        <button className={close_button} onClick={onModalClose}>
          <Icon type="arrow" name="medium-left" />
          <span>Return to listing</span>
        </button>
        <div className={address}>
          <p>{property.address}</p>
        </div>
        <div className={social_buttons}>
          <span className={like_button} role="button" tabIndex="0">
            <LikeButton header={true} id={property.listingId} />
          </span>
          <span className={share_button}>
            <Button
              icon="share"
              iconType="actions"
              label="Share"
              theme="text-only"
              onClick={() => setShareModalIsOpen(true)}
            />
          </span>
        </div>
      </div>
      <div className={modal_inner}>
        <div>{content || children}</div>
      </div>
      {shareModalIsOpen && (
        <ShareModal
          modalIsOpen={shareModalIsOpen}
          onModalClose={() => setShareModalIsOpen(false)}
          sharingServicesCollection={sharingServicesCollection}
          copyUrl={canonicalUrl}
        />
      )}
    </ReactModal>
  );
};

FullScreenModal.propTypes = {
  /**
   * Specifies the modal button
   */
  button: PropTypes.node,

  /**
   * Canonical Url to be displayed and copied onto the clipboard
   */
  canonicalUrl: PropTypes.string,

  /**
   * Specifies the modal content
   */
  children: PropTypes.node,

  /**
   * Specifies the modal content
   */
  content: PropTypes.node,

  /**
   * Specifies the content label on the modal.
   */
  contentLabel: PropTypes.string,

  /**
   * Specifies whether the modal is currently open
   */
  modalIsOpen: PropTypes.bool,

  /**
   * Function that will be run after the modal has closed.
   */
  onModalClose: PropTypes.func,

  /**
   * Specifies whether the page has a chat button open
   */
  pageHasChat: PropTypes.bool,

  /**
   * Specifies the details of a property
   */
  property: PropTypes.shape({
    address: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string
      })
    ),
    listingId: PropTypes.number,
    listingUrl: PropTypes.string,
    status: PropTypes.arrayOf(
      PropTypes.shape({
        newConstruction: PropTypes.bool,
        newListing: PropTypes.bool,
        openHouse: PropTypes.bool,
        pending: PropTypes.bool,
        priceChange: PropTypes.bool
      })
    )
  }),

  /**
   * Collection of share items to be displayed within the modal
   */
  sharingServicesCollection: PropTypes.arrayOf(
    PropTypes.shape({
      shareName: PropTypes.string,
      shareUrl: PropTypes.string
    })
  )
};

export default FullScreenModal;
